/**
 * StateField that provides block-level inline render decorations.
 *
 * In CodeMirror 6, Decoration.widget({block: true}) can ONLY be provided by
 * StateFields, not ViewPlugins. This field reads the scan, inline-render set,
 * and ref-resolve context from sibling StateFields to produce the block widgets.
 */

import { EditorState, RangeSetBuilder, StateField } from "@codemirror/state";
import {
    Decoration,
    type DecorationSet,
    EditorView,
} from "@codemirror/view";
import type { ContentDefinition, PluginDefinition } from "@magic-md/core";
import { useEditorStore } from "../../../store/editor-store";
import { FOR_CLOSE_RE, FOR_HEADER_RE } from "../patterns";
import { magicScanField } from "../scan-state";
import {
    inlineRenderedDefinitionsField,
    refResolveContextField,
    setInlineRenderedDefinitions,
    setRefResolveContext,
} from "../state-fields";
import { createRefResolveContext } from "../ref/resolution";
import { InlineRenderWidget } from "./inline-render-widget";

function buildInlineRenderDefinitions(
    refCtx: ReturnType<typeof createRefResolveContext>,
): Map<string, PluginDefinition> {
    const definitions = new Map(refCtx.parsedDoc?.definitions ?? new Map());
    for (const [id, definition] of refCtx.engineDefinitions ?? new Map()) {
        definitions.set(id, definition);
    }
    return definitions;
}

function buildInlineRenderContentDefinitions(
    refCtx: ReturnType<typeof createRefResolveContext>,
): Map<string, ContentDefinition> {
    const contentDefinitions = new Map(
        refCtx.parsedDoc?.contentDefinitions ?? new Map(),
    );
    for (const [id, definition] of refCtx.engineContentDefinitions ?? new Map()) {
        contentDefinitions.set(id, definition);
    }
    return contentDefinitions;
}

function computeInlineRenderBlockDecorations(
    state: EditorState,
): DecorationSet {
    const scan = state.field(magicScanField).scan;
    const inlineRenderedIds =
        state.field(inlineRenderedDefinitionsField, false) ?? new Set<string>();

    if (inlineRenderedIds.size === 0) return Decoration.none;

    const refCtx =
        state.field(refResolveContextField, false) ?? createRefResolveContext();
    const inlineRenderDefinitions = buildInlineRenderDefinitions(refCtx);
    const inlineRenderContentDefinitions =
        buildInlineRenderContentDefinitions(refCtx);
    const inlineRenderDocumentPath =
        useEditorStore.getState().tabs.find(
            (tab) => tab.id === useEditorStore.getState().activeTabId,
        )?.filePath ?? null;

    const doc = state.doc;
    const targets: Array<{ blockId: string; endLine: number; pos: number }> = [];

    function collectBlockWidget(blockId: string, endLine: number) {
        if (!inlineRenderedIds.has(blockId)) return;
        const line = doc.line(endLine);
        targets.push({ blockId, endLine, pos: line.to });
    }

    for (const defBlock of scan.defineBlocks) {
        collectBlockWidget(defBlock.defId, defBlock.endLine);
    }
    for (const contentBlock of scan.contentBlocks) {
        collectBlockWidget(contentBlock.defId, contentBlock.endLine);
    }
    for (const useBlock of scan.useBlocks) {
        collectBlockWidget(useBlock.useId, useBlock.endLine);
    }
    const forStack: string[] = [];
    for (let i = 1; i <= doc.lines; i++) {
        const trimmed = doc.line(i).text.trimStart();
        const forMatch = trimmed.match(FOR_HEADER_RE);
        if (forMatch) {
            forStack.push(forMatch[1]);
            continue;
        }
        if (FOR_CLOSE_RE.test(trimmed)) {
            const forId = forStack.pop();
            if (forId) collectBlockWidget(forId, i);
        }
    }

    targets.sort((a, b) => a.pos - b.pos || a.endLine - b.endLine);

    const builder = new RangeSetBuilder<Decoration>();
    for (const target of targets) {
        // Place the block widget AFTER the block's close line (:: or ::endfor)
        // so the inline render appears below the entire block, not just the header.
        builder.add(
            target.pos,
            target.pos,
            Decoration.widget({
                widget: new InlineRenderWidget(
                    target.blockId,
                    inlineRenderDefinitions,
                    inlineRenderContentDefinitions,
                    inlineRenderDocumentPath,
                ),
                block: true,
                side: 2,
            }),
        );
    }

    return builder.finish();
}

export const inlineRenderBlockField = StateField.define<DecorationSet>({
    create: () => Decoration.none,
    update(value, tr) {
        let needsRecompute = false;
        for (const e of tr.effects) {
            if (
                e.is(setInlineRenderedDefinitions) ||
                e.is(setRefResolveContext)
            ) {
                needsRecompute = true;
                break;
            }
        }

        if (tr.docChanged) needsRecompute = true;

        if (!needsRecompute) return value;

        return computeInlineRenderBlockDecorations(tr.state);
    },
    provide: (field) => EditorView.decorations.from(field),
});
